import type {
  BillingPlan,
  BillingProviderAdapter,
  Invoice,
  PaymentMethod,
  SubscriptionStatus,
} from "../types";

const MOCK_PLANS: BillingPlan[] = [
  {
    id: "free",
    name: "Free",
    priceLabel: "$0/mo",
    interval: "month",
    description: "Basic usage for personal automation.",
    featureList: ["1 project", "Community support"],
  },
  {
    id: "pro",
    name: "Pro",
    priceLabel: "$19/mo",
    interval: "month",
    description: "Production automation with alerts.",
    featureList: ["Unlimited projects", "Priority support", "Advanced entitlements"],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    priceLabel: "$99/mo",
    interval: "month",
    description: "Team-scale automation with SLAs.",
    featureList: ["Unlimited projects", "Dedicated support", "SSO", "Audit log", "Custom SLA"],
  },
];

type MockState = {
  planId: string;
  status: SubscriptionStatus["status"];
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string;
  invoices: Invoice[];
  paymentMethods: PaymentMethod[];
  nextInvoiceId: number;
  nextPaymentMethodId: number;
};

function createInitialState(): MockState {
  const periodEnd = new Date();
  periodEnd.setDate(periodEnd.getDate() + 30);

  return {
    planId: "pro",
    status: "active",
    cancelAtPeriodEnd: false,
    currentPeriodEnd: periodEnd.toISOString(),
    invoices: [
      {
        id: "inv_001",
        date: new Date(Date.now() - 30 * 86400000).toISOString(),
        amount: "$19.00",
        status: "paid",
        description: "Pro plan — monthly",
      },
      {
        id: "inv_002",
        date: new Date(Date.now() - 60 * 86400000).toISOString(),
        amount: "$19.00",
        status: "paid",
        description: "Pro plan — monthly",
      },
    ],
    paymentMethods: [
      {
        id: "pm_001",
        type: "card",
        label: "Visa ending in 4242",
        isDefault: true,
      },
    ],
    nextInvoiceId: 3,
    nextPaymentMethodId: 2,
  };
}

function getPlanPrice(planId: string): string {
  const plan = MOCK_PLANS.find((p) => p.id === planId);
  if (!plan) return "$0.00";
  const match = plan.priceLabel.match(/\$(\d+)/);
  return match ? `$${match[1]}.00` : "$0.00";
}

export type MockAdapterOptions = {
  initialPlanId?: string;
  initialStatus?: SubscriptionStatus["status"];
  latencyMs?: number;
};

export function createMockAdapter(options: MockAdapterOptions = {}): BillingProviderAdapter {
  const state: MockState = createInitialState();
  if (options.initialPlanId) state.planId = options.initialPlanId;
  if (options.initialStatus) state.status = options.initialStatus;

  const latency = options.latencyMs ?? 300;

  async function delay(): Promise<void> {
    if (latency > 0) {
      await new Promise((resolve) => setTimeout(resolve, latency));
    }
  }

  return {
    name: "mock",
    capabilities: {
      inlinePortal: true,
      invoices: true,
      paymentMethods: true,
    },

    async getPlans() {
      await delay();
      return MOCK_PLANS;
    },

    async getCurrentEntitlement(_userId: string) {
      await delay();
      return {
        tier:
          state.planId === "free" ? "free" : state.planId === "enterprise" ? "enterprise" : "pro",
        features: {
          automations: state.planId !== "free",
          projects: state.planId === "free" ? 1 : state.planId === "pro" ? 100 : 9999,
        },
        limits: {
          members: state.planId === "free" ? 1 : state.planId === "pro" ? 25 : 500,
        },
        expiresAt: state.status === "active" ? state.currentPeriodEnd : null,
      };
    },

    async startCheckout(planId: string, returnUrl: string) {
      await delay();
      state.planId = planId;
      state.status = "active";
      state.cancelAtPeriodEnd = false;
      return { checkoutUrl: `${returnUrl}?checkout=mock&plan=${planId}` };
    },

    async openCustomerPortal(returnUrl: string) {
      await delay();
      return { portalUrl: `${returnUrl}?portal=mock` };
    },

    async handleCallback() {
      await delay();
      return { ok: true, message: "Mock callback handled." };
    },

    async getSubscriptionStatus(_userId: string) {
      await delay();
      return {
        active: state.status === "active" || state.status === "trialing",
        status: state.status,
        currentPeriodEnd: state.currentPeriodEnd,
        planId: state.planId,
        cancelAtPeriodEnd: state.cancelAtPeriodEnd,
      };
    },

    async changePlan(_userId: string, newPlanId: string) {
      await delay();
      const plan = MOCK_PLANS.find((p) => p.id === newPlanId);
      if (!plan) return { ok: false, message: "Plan not found." };

      const previousPlanId = state.planId;
      state.planId = newPlanId;
      state.status = newPlanId === "free" ? "canceled" : "active";
      state.cancelAtPeriodEnd = false;

      if (newPlanId !== "free") {
        state.invoices.unshift({
          id: `inv_${String(state.nextInvoiceId++).padStart(3, "0")}`,
          date: new Date().toISOString(),
          amount: getPlanPrice(newPlanId),
          status: "paid",
          description: `Plan change: ${previousPlanId} -> ${newPlanId}`,
        });
      }

      return { ok: true, message: `Switched to ${plan.name} plan.` };
    },

    async cancelSubscription(_userId: string) {
      await delay();
      if (state.planId === "free")
        return { ok: false, message: "No active subscription to cancel." };
      state.cancelAtPeriodEnd = true;
      return { ok: true, message: "Subscription will cancel at end of billing period." };
    },

    async resumeSubscription(_userId: string) {
      await delay();
      if (!state.cancelAtPeriodEnd)
        return { ok: false, message: "Subscription is not scheduled for cancellation." };
      state.cancelAtPeriodEnd = false;
      return { ok: true, message: "Subscription resumed." };
    },

    async getInvoices(_userId: string) {
      await delay();
      return state.invoices;
    },

    async getPaymentMethods(_userId: string) {
      await delay();
      return state.paymentMethods;
    },

    async addPaymentMethod(_userId: string, token: string) {
      await delay();
      const lastFour = token.slice(-4) || "0000";
      const pm: PaymentMethod = {
        id: `pm_${String(state.nextPaymentMethodId++).padStart(3, "0")}`,
        type: "card",
        label: `Card ending in ${lastFour}`,
        isDefault: state.paymentMethods.length === 0,
      };
      state.paymentMethods.push(pm);
      return { ok: true, paymentMethod: pm };
    },

    async removePaymentMethod(_userId: string, paymentMethodId: string) {
      await delay();
      const idx = state.paymentMethods.findIndex((pm) => pm.id === paymentMethodId);
      if (idx === -1) return { ok: false };
      const wasDefault = state.paymentMethods[idx].isDefault;
      state.paymentMethods.splice(idx, 1);
      if (wasDefault && state.paymentMethods.length > 0) {
        state.paymentMethods[0].isDefault = true;
      }
      return { ok: true };
    },

    async setDefaultPaymentMethod(_userId: string, paymentMethodId: string) {
      await delay();
      for (const pm of state.paymentMethods) {
        pm.isDefault = pm.id === paymentMethodId;
      }
      return { ok: true };
    },
  };
}
